import type { EdgeModifier } from "building-types/modifiers/edgeModifierTypes";
import type { GeneratedModifier } from "building-types/modifiers/generatedModifier";
import type { RWTable, Table } from "common/data/table";
import { polyLine2Copy, polyLine2IsClosed, polyLine2MoveVertices } from "geom/polyLine2";
import type { PolyLine2 } from "geom/polyLine2Types";

export type EdgeModifiersByParentId = Map<string, Table<EdgeModifier>>;

const EMPTY_EDGE_MODIFIERS_TABLE: Table<EdgeModifier> = Object.freeze({});

export function buildEdgeModifiersByParentId(edgeModifiers: Table<EdgeModifier>): EdgeModifiersByParentId {
  const index = new Map<string, RWTable<EdgeModifier>>();
  for (const edgeModifierId in edgeModifiers) {
    const edgeModifier = edgeModifiers[edgeModifierId];
    const { parentId } = edgeModifier;
    let parentTable = index.get(parentId);
    if (!parentTable) {
      parentTable = {};
      index.set(parentId, parentTable);
    }
    parentTable[edgeModifierId] = edgeModifier;
  }
  return index;
}

export function getEntityEdgeModifiersTable(
  edgeModifiersByParentId: EdgeModifiersByParentId,
  entityId: string,
): Table<EdgeModifier> {
  return edgeModifiersByParentId.get(entityId) ?? EMPTY_EDGE_MODIFIERS_TABLE;
}

function collectMatchingEdgeModifierIds(
  edgeModifiers: Table<EdgeModifier>,
  predicate: (edgeModifier: EdgeModifier) => boolean,
): string[] {
  const ids: string[] = [];
  for (const edgeModifierId in edgeModifiers) {
    const edgeModifier = edgeModifiers[edgeModifierId];
    if (predicate(edgeModifier)) {
      ids.push(edgeModifierId);
    }
  }
  return ids;
}

export function getEdgeModifierIdsOfModifierId(edgeModifiers: Table<EdgeModifier>, modifierId: string): string[] {
  return collectMatchingEdgeModifierIds(edgeModifiers, (edgeModifier) => edgeModifier.modifierId === modifierId);
}

export function getEdgeModifierIdsOfParentIdAndModifierId(
  edgeModifiersByParentId: EdgeModifiersByParentId,
  parentId: string,
  modifierId: string,
): string[] {
  return collectMatchingEdgeModifierIds(
    getEntityEdgeModifiersTable(edgeModifiersByParentId, parentId),
    (edgeModifier) => edgeModifier.modifierId === modifierId,
  );
}

export function getEdgeModifierIdsOfParentIdAndModifierIdAndEdgeId(
  edgeModifiersByParentId: EdgeModifiersByParentId,
  parentId: string | null,
  modifierId: string,
  edgeId: string,
): string[] {
  if (parentId === null) {
    return [];
  }

  return collectMatchingEdgeModifierIds(
    getEntityEdgeModifiersTable(edgeModifiersByParentId, parentId),
    (edgeModifier) => edgeModifier.modifierId === modifierId && edgeModifier.edgeId === edgeId,
  );
}

export function polyLine2Modify(
  outline: PolyLine2,
  edgeModifiers: Table<EdgeModifier>,
  modifiers: Table<GeneratedModifier>,
  edgeIds: string[],
) {
  if (!edgeModifiers || !modifiers) {
    return outline;
  }
  const numVertices = polyLine2IsClosed(outline) ? edgeIds.length : edgeIds.length + 1;
  let mutableOutline = polyLine2Copy(outline);
  const edgeVertexHashTable: { [hash: string]: boolean } = {};

  for (const edgeModifierId in edgeModifiers) {
    const edgeModifier = edgeModifiers[edgeModifierId];
    if (!edgeModifier) {
      continue;
    }

    const modifier = modifiers[edgeModifier.modifierId];
    if (!modifier) {
      continue;
    }

    const edgeIndex = edgeIds.indexOf(edgeModifier.edgeId);
    if (edgeIndex < 0) {
      continue;
    }

    const vertexStartHash = edgeIndex + "_" + edgeModifier.modifierId;
    const vertexEndHash = ((edgeIndex + 1) % numVertices) + "_" + edgeModifier.modifierId;

    if (!edgeVertexHashTable[vertexStartHash]) {
      edgeVertexHashTable[vertexStartHash] = true;

      mutableOutline = polyLine2MoveVertices(mutableOutline, [edgeIndex], modifier.moveX, modifier.moveY);
    }

    if (!edgeVertexHashTable[vertexEndHash]) {
      edgeVertexHashTable[vertexEndHash] = true;
      mutableOutline = polyLine2MoveVertices(
        mutableOutline,
        [(edgeIndex + 1) % numVertices],
        modifier.moveX,
        modifier.moveY,
      );
    }
  }
  return mutableOutline;
}

/**
 *
 * @param outline room outline
 * @param edgeModifiers filtered edge modifiers belonging to the room
 * @param edgeIds edge ids belonging to the room
 * @param moveX distance to move in x direction
 * @param moveY distance to move in y direction
 * @returns
 */
export function mutateRoomOutlineUsingEdgeModifiers(
  outline: PolyLine2,
  edgeModifiers: EdgeModifier[],
  edgeIds: string[],
  moveX: number,
  moveY: number,
) {
  if (edgeModifiers.length === 0) {
    return outline;
  }
  const numVertices = polyLine2IsClosed(outline) ? edgeIds.length : edgeIds.length + 1;
  let mutatedOutline = polyLine2Copy(outline);
  const edgeVertexHashTable: { [hash: string]: boolean } = {};

  for (const edgeModifierId of Object.keys(edgeModifiers)) {
    const edgeModifier = edgeModifiers[edgeModifierId];
    if (!edgeModifier) {
      continue;
    }

    const edgeIndex = edgeIds.indexOf(edgeModifier.edgeId);
    if (edgeIndex < 0) {
      continue;
    }

    const vertexStartHash = edgeIndex + "_" + edgeModifier.modifierId;
    const vertexEndHash = ((edgeIndex + 1) % numVertices) + "_" + edgeModifier.modifierId;

    if (!edgeVertexHashTable[vertexStartHash]) {
      edgeVertexHashTable[vertexStartHash] = true;

      mutatedOutline = polyLine2MoveVertices(mutatedOutline, [edgeIndex], moveX, moveY);
    }

    if (!edgeVertexHashTable[vertexEndHash]) {
      edgeVertexHashTable[vertexEndHash] = true;
      mutatedOutline = polyLine2MoveVertices(mutatedOutline, [(edgeIndex + 1) % numVertices], moveX, moveY);
    }
  }
  return mutatedOutline;
}
